/**
 * Phase 27.50.B.14 — Persistent gold banner shown across the app
 * whenever the user's Google Calendar OAuth grant is broken
 * (`needs_reconnect=true`).
 *
 * Polls `/api/integrations/google/status` every 5 minutes. The banner
 * is dismissible per session; the dismissal cache expires after 6
 * hours so the reminder resurfaces if the connection is still broken.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { googleCalendarAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const DISMISS_KEY = 'kr:calendar_reconnect_dismissed_at';
const DISMISS_TTL_MS = 6 * 60 * 60 * 1000;   // 6h
const POLL_MS = 5 * 60 * 1000;                // 5 min

function _dismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_TTL_MS;
  } catch { return false; }
}

export default function CalendarReconnectBanner() {
  const { user } = useAuth();
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [dismissed, setDismissed] = useState(_dismissedRecently());

  const check = useCallback(async () => {
    if (!user) return;
    try {
      const r = await googleCalendarAPI.status();
      setNeedsReconnect(!!r.data?.needs_reconnect);
    } catch { /* silent — auth error or offline is fine */ }
  }, [user]);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, [check]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  if (!user || !needsReconnect || dismissed) return null;

  return (
    <div
      role="alert"
      data-testid="calendar-reconnect-banner"
      className="w-full flex items-center gap-3 px-4 py-2.5 bg-amber-100 border-b border-amber-300 text-amber-900 text-sm"
    >
      <AlertTriangle size={16} className="flex-shrink-0" />
      <div className="flex-1">
        <strong>Google Calendar disconnected.</strong>
        <span className="ml-1 hidden sm:inline">
          Appointments, event syncs and reminders are paused until you reconnect.
        </span>
      </div>
      <Link
        to="/calendar/settings"
        className="px-3 py-1 bg-amber-900 text-white rounded-md text-xs font-semibold hover:bg-amber-800 transition-colors"
        data-testid="calendar-reconnect-cta"
      >
        Reconnect
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss for 6 hours"
        className="p-1 rounded hover:bg-amber-200 transition-colors"
        data-testid="calendar-reconnect-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
